import {useEffect, useRef, useState} from 'react'
import {SHOW_ALL_OFFERS_PARAM} from 'constants/index'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {stripUrlParamsFromHistory} from 'utils/url'

import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'

interface Params {
  hotelId: string
  /** Same gate that renders the "More deals" button */
  hasMoreOffers: boolean
}

/**
 * Opens the all-offers overlay when `showAllOffers=1` is in the URL and
 * strips the param after consuming it.
 */
export const useShowAllOffersFromUrl = ({hotelId, hasMoreOffers}: Params) => {
  const {getQueryParams} = useRouterProvider()
  const showOverlay = useShowOverlay()
  // State so the open effect re-runs when the intent flips
  const [wantsAllOffers, setWantsAllOffers] = useState(false)
  const hasOpenedRef = useRef(false)

  const paramValue = (getQueryParams?.() || {})[SHOW_ALL_OFFERS_PARAM]
  const hasParam = typeof paramValue !== 'undefined'

  useEffect(() => {
    if (!hasParam) return

    // Strip any value, or it lingers in the URL
    if (paramValue === '1') setWantsAllOffers(true)
    stripUrlParamsFromHistory([SHOW_ALL_OFFERS_PARAM])
  }, [hasParam, paramValue])

  // Runs after the strip so showOverlay sees the clean URL
  useEffect(() => {
    if (hasOpenedRef.current || !wantsAllOffers || !hasMoreOffers) return

    hasOpenedRef.current = true
    showOverlay({
      type: OverlayType.HotelAllOffers,
      params: {hotelId}
    })
  }, [wantsAllOffers, hasMoreOffers, hotelId, showOverlay])
}
